"use client";

import React, { useState, useEffect } from "react";
import { TrendingUp, Activity, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const analyticsData = [{
  month: "Jan",
  views: 400,
  engagements: 240
}, {
  month: "Feb",
  views: 600,
  engagements: 380
}, {
  month: "Mar",
  views: 800,
  engagements: 520
}, {
  month: "Apr",
  views: 1100,
  engagements: 780
}, {
  month: "May",
  views: 1400,
  engagements: 1020
}, {
  month: "Jun",
  views: 1800,
  engagements: 1400
}];
const seoMetrics = [{
  label: "Purity Threshold",
  value: 99
}, {
  label: "Batch Consistency",
  value: 99
}, {
  label: "Stability Index",
  value: 99
}];
const chartConfig = {
  views: {
    label: "Purity %",
    color: "hsl(43 60% 60%)"
  },
  engagements: {
    label: "Stability",
    color: "hsl(0 0% 100%)"
  }
};
export default function SeoAndAnalytics() {
  const {
    ref: analyticsRef,
    isVisible: analyticsVisible
  } = useRevealOnScroll();
  const {
    ref: seoRef,
    isVisible: seoVisible
  } = useRevealOnScroll();
  const [readabilityScore, setReadabilityScore] = useState(99);
  return <section data-section-id="235808" id="the-aurelian-standard" className="bg-background text-foreground py-16 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h2 className="font-default text-3xl md:text-4xl font-bold mb-3 tracking-tight uppercase text-foreground">
            The Aurelian Standard
          </h2>
          <p className="font-default text-muted-foreground text-lg max-w-2xl leading-relaxed">
            Uncompromising precision in biotechnology. We maintain the industry's most rigorous verification protocols for research-grade compounds.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div ref={analyticsRef} className="lg:col-span-2">
            <Card className="border border-border bg-card/40 backdrop-blur-md h-full rounded-none shadow-2xl">
              <CardHeader>
                <CardTitle className="font-default flex items-center gap-2 text-foreground uppercase tracking-wider text-sm">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Molecular Purity Analysis
                </CardTitle>
                <CardDescription className="text-muted-foreground font-default">
                  HPLC verification and stability metrics across production cycles
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData} margin={{
                    top: 5,
                    right: 30,
                    left: 0,
                    bottom: 5
                  }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 5% 15%)" />
                      <XAxis dataKey="month" stroke="hsl(240 5% 45%)" className="font-mono text-xs" />
                      <YAxis stroke="hsl(240 5% 45%)" className="font-mono text-xs" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line type="monotone" dataKey="views" stroke="var(--color-views)" strokeWidth={1.5} dot={{
                      fill: "var(--color-views)",
                      r: 3
                    }} />
                      <Line type="monotone" dataKey="engagements" stroke="var(--color-engagements)" strokeWidth={1.5} dot={{
                      fill: "var(--color-engagements)",
                      r: 3
                    }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <div ref={seoRef} className="lg:col-span-1">
            <Card className="border border-border bg-card/40 backdrop-blur-md h-full flex flex-col rounded-none shadow-2xl">
              <CardHeader>
                <CardTitle className="font-default flex items-center gap-2 text-foreground uppercase tracking-wider text-sm">
                  <Zap className="w-5 h-5 text-primary" />
                  Quality Control
                </CardTitle>
                <CardDescription className="text-muted-foreground font-default">
                  Real-time batch validation score
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">Batch Purity</span>
                    <span className="font-mono text-xl font-bold text-primary">
                      {readabilityScore}%
                    </span>
                  </div>
                  <Progress value={readabilityScore} className="h-1 bg-secondary" />
                </div>

                <div className="space-y-3">
                  {seoMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-default text-xs text-muted-foreground uppercase tracking-tighter">
                          {metric.label}
                        </span>
                        <span className="font-mono text-xs font-medium text-foreground">
                          {metric.value}%
                        </span>
                      </div>
                      <Progress value={metric.value} className="h-1 bg-secondary" />
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{
          icon: Activity,
          title: "3rd-Party HPLC Testing",
          description: "Every batch undergoes independent high-performance liquid chromatography to ensure >99% purity."
        }, {
          icon: TrendingUp,
          title: "Cold-Chain Logistics",
          description: "Temperature-controlled shipping environments preserve the structural integrity of sensitive peptides."
        }, {
          icon: Zap,
          title: "Batch Traceability",
          description: "Full institutional transparency with unique identifiers for every compound synthesized in our labs."
        }].map((feature, i) => <Card key={feature.title} data-index={i} className="border-t-2 border-t-primary border-x-0 border-b-0 bg-card/20 backdrop-blur-sm hover:bg-card/40 transition-all duration-500 rounded-none">
              <CardContent className="pt-6">
                <feature.icon className="w-8 h-8 mb-4 text-primary" />
                <h3 className="font-default font-bold mb-2 text-foreground uppercase tracking-tight">
                  {feature.title}
                </h3>
                <p className="font-default text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}